import type { ParseType, ParsedImageInfo } from '../types';

/**
 * 文档解析请求参数
 */
export interface DocumentParseRequest {
    /** 文档文件（支持PDF和DOCX格式，最大5MB） */
    file: File;
    /** 解析类型：text_only(只解析文本), image_only(只解析图片), both(解析文本和图片) */
    parse_type: ParseType;
    /** 文件夹路径，用于组织文件存储结构 */
    folder_path?: string;
    /** 文件名前缀，用于标识文件用途 */
    file_prefix?: string;
    /** 是否生成唯一文件名 */
    generate_unique_name?: boolean;
}

/**
 * 文档解析响应
 */
export interface DocumentParseResponse {
    /** 解析类型 */
    parse_type: ParseType;
    /** 原始文件名 */
    original_file_name: string;
    /** 文件大小（字节） */
    file_size: number;
    /** 文件类型 */
    file_type: string;
    /** 解析的文本内容 */
    text_content: string;
    /** 解析的图片信息列表 */
    images: ParsedImageInfo[];
    /** 解析状态 */
    status: string;
    /** 解析耗时（毫秒） */
    processing_time: number;
}

/**
 * Smart Document 解析请求参数
 */
export interface SmartDocumentParseRequest {
    /** 文档文件（支持PDF、DOC和DOCX格式） */
    file: File;
}

/**
 * Smart Document 解析状态
 */
export type SmartDocumentParseStatus = 'SUCCESS' | 'PARTIAL_SUCCESS' | 'FAILED';

/**
 * Smart Document 文档解析块
 */
export interface SmartDocumentParsedBlock {
    /** 文档块序号 */
    index?: number;
    /** 块类型 */
    type?: string;
    /** 块分类 */
    category?: string;
    /** 页码 */
    page?: number;
    /** 归一化坐标 */
    bbox?: number[];
    /** 文本内容 */
    content?: string;
    /** 我方桶路径 */
    s3_key?: string;
    /** 我方签名访问地址 */
    signed_url?: string;
    /** 图片搬运状态 */
    upload_status?: string;
    /** 错误信息 */
    error_message?: string;
}

/**
 * Smart Document 图片信息
 */
export interface SmartDocumentParsedImageInfo {
    /** 对应文档块序号 */
    index?: number;
    /** 页码 */
    page?: number;
    /** 块分类 */
    category?: string;
    /** 归一化坐标 */
    bbox?: number[];
    /** 我方桶路径 */
    s3_key: string;
    /** 我方签名访问地址 */
    signed_url: string;
    /** 我方桶内文件名 */
    file_name?: string;
    /** 文件大小（字节） */
    file_size?: number;
    /** 文件 Content-Type */
    content_type?: string;
}

/**
 * Smart Document 图片搬运失败信息
 */
export interface SmartDocumentFailedImageInfo {
    /** 对应文档块序号 */
    index?: number;
    /** 页码 */
    page?: number;
    /** 块分类 */
    category?: string;
    /** 错误信息 */
    error_message?: string;
}

/**
 * Smart Document 解析响应
 */
export interface SmartDocumentParseResponse {
    /** 解析状态：SUCCESS、PARTIAL_SUCCESS、FAILED */
    status: SmartDocumentParseStatus;
    /** 原始文件名 */
    original_file_name?: string;
    /** 文件大小（字节） */
    file_size?: number;
    /** 解析耗时（毫秒） */
    processing_time_ms?: number;
    /** 按文档顺序拼接后的文本 */
    text?: string;
    /** 解析后的 Markdown 内容 */
    markdown?: string;
    /** 文档解析块 */
    blocks?: SmartDocumentParsedBlock[];
    /** 成功搬运到我方桶的图片 */
    images?: SmartDocumentParsedImageInfo[];
    /** 搬运失败的图片 */
    failed_images?: SmartDocumentFailedImageInfo[];
}

export const DocumentParseApi = Symbol.for('DocumentParseApi');

/**
 * 文档解析 API 接口
 * @description 提供文档解析相关的 API 调用
 */
export interface DocumentParseApi {
    /**
     * 解析PDF或Word文档
     * @param request 解析请求参数
     * @returns 文档解析响应
     */
    parseDocument(request: DocumentParseRequest): Promise<DocumentParseResponse>;

    /**
     * 通过 Smart Document 解析PDF或Word文档
     * @param request 解析请求参数
     * @returns Smart Document 文档解析响应
     */
    smartParseDocument(request: SmartDocumentParseRequest): Promise<SmartDocumentParseResponse>;
}
